// User waypoint store — local-first IDB + opt-in Supabase sync.
// Mirrors pilotStore.ts for shape and sync semantics.
//
// Waypoints are created by the pilot via the map long-press picker
// ("Save waypoint" action on the Coordinates row). They appear as
// flag markers on the map and can be added to the active flight plan.

import { create } from 'zustand';
import { createKvList } from '../lib/kvList';
import { supabase } from '../lib/supabase';
import { useAuthStore } from './authStore';
import { newClientId } from '../lib/ids';

// ───────────────────── Types ─────────────────────

export interface UserWaypoint {
  /** Local client_id — wpt-<ts>-<rand> */
  id: string;
  name: string;
  lat: number;
  lon: number;
  kind: string;       // always 'user' for now; reserved for sub-types
  notes?: string;
  createdAt: number;
  updatedAt: number;
}

// ───────────────────── DB row ─────────────────────

interface WaypointRowDb {
  id: string;
  owner_id: string;
  client_id: string | null;
  name: string;
  lat: number;
  lon: number;
  kind: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// ───────────────────── Helpers ─────────────────────

const { loadAll, saveAll } = createKvList<UserWaypoint>('waypoints');

function toDbRow(w: UserWaypoint, ownerId: string): {
  owner_id: string;
  client_id: string;
  name: string;
  lat: number;
  lon: number;
  kind: string;
  notes: string | null;
} {
  return {
    owner_id: ownerId,
    client_id: w.id,
    name: w.name,
    lat: w.lat,
    lon: w.lon,
    kind: w.kind,
    notes: w.notes ?? null,
  };
}

function fromDbRow(row: WaypointRowDb): UserWaypoint {
  const localId = row.client_id ?? row.id;
  return {
    id: localId,
    name: row.name,
    lat: row.lat,
    lon: row.lon,
    kind: row.kind ?? 'user',
    notes: row.notes ?? undefined,
    createdAt: row.created_at ? new Date(row.created_at).getTime() : Date.now(),
    updatedAt: row.updated_at ? new Date(row.updated_at).getTime() : Date.now(),
  };
}

/** Generate a default waypoint name from lat/lon.
 *  e.g. WPT-N424W122 for lat≈42.4, lon≈-122 */
export function defaultWaypointName(lat: number, lon: number): string {
  const latH = lat >= 0 ? 'N' : 'S';
  const lonH = lon >= 0 ? 'E' : 'W';
  const latInt = Math.abs(Math.round(lat * 10));
  const lonInt = Math.abs(Math.round(lon * 10));
  return `WPT-${latH}${latInt}${lonH}${lonInt}`;
}

// ───────────────────── Store ─────────────────────

interface WaypointState {
  list: UserWaypoint[];
  hydrated: boolean;
  lastSyncAt: number | null;
  syncing: boolean;
  syncError: string | null;

  hydrate: () => Promise<void>;
  add: (input: Pick<UserWaypoint, 'name' | 'lat' | 'lon'> & Partial<UserWaypoint>) => Promise<UserWaypoint>;
  update: (id: string, patch: Partial<UserWaypoint>) => Promise<void>;
  remove: (id: string) => Promise<void>;

  syncFromServer: () => Promise<void>;
  syncToServer: () => Promise<void>;

  /** Find the nearest waypoint to a given lat/lon within thresholdNm. */
  nearest: (lat: number, lon: number, thresholdNm?: number) => UserWaypoint | null;
  /** Waypoints within `radiusNm` nautical miles, sorted by distance. */
  within: (lat: number, lon: number, radiusNm: number) => Array<UserWaypoint & { distanceNm: number }>;
}

async function pushOne(
  wpt: UserWaypoint,
  set: (s: Partial<WaypointState>) => void,
): Promise<void> {
  if (!supabase) return;
  const user = useAuthStore.getState().user;
  if (!user) return;
  try {
    const row = toDbRow(wpt, user.id);
    const { error } = await supabase
      .from('user_waypoints')
      .upsert(row, { onConflict: 'owner_id,client_id' });
    if (error) set({ syncError: error.message });
    else set({ syncError: null, lastSyncAt: Date.now() });
  } catch (e: unknown) {
    set({ syncError: e instanceof Error ? e.message : String(e) });
  }
}

async function deleteOne(
  clientId: string,
  set: (s: Partial<WaypointState>) => void,
): Promise<void> {
  if (!supabase) return;
  const user = useAuthStore.getState().user;
  if (!user) return;
  try {
    const { error } = await supabase
      .from('user_waypoints')
      .delete()
      .eq('owner_id', user.id)
      .eq('client_id', clientId);
    if (error) set({ syncError: error.message });
    else set({ syncError: null, lastSyncAt: Date.now() });
  } catch (e: unknown) {
    set({ syncError: e instanceof Error ? e.message : String(e) });
  }
}

export const useWaypointStore = create<WaypointState>((set, get) => ({
  list: [],
  hydrated: false,
  lastSyncAt: null,
  syncing: false,
  syncError: null,

  hydrate: async () => {
    if (get().hydrated) return;
    const list = await loadAll();
    set({ list, hydrated: true });
    // If already signed in at hydrate-time, sync.
    if (useAuthStore.getState().session) {
      void get().syncFromServer();
    }
  },

  add: async (input) => {
    const now = Date.now();
    const wpt: UserWaypoint = {
      id: input.id ?? newClientId('wpt'),
      name: input.name,
      lat: input.lat,
      lon: input.lon,
      kind: input.kind ?? 'user',
      notes: input.notes,
      createdAt: now,
      updatedAt: now,
    };
    const next = [...get().list, wpt];
    await saveAll(next);
    set({ list: next });
    void pushOne(wpt, set);
    return wpt;
  },

  update: async (id, patch) => {
    const list = get().list;
    const idx = list.findIndex((w) => w.id === id);
    if (idx === -1) return;
    const updated: UserWaypoint = { ...list[idx]!, ...patch, updatedAt: Date.now() };
    const next = [...list.slice(0, idx), updated, ...list.slice(idx + 1)];
    await saveAll(next);
    set({ list: next });
    void pushOne(updated, set);
  },

  remove: async (id) => {
    const list = get().list;
    const next = list.filter((w) => w.id !== id);
    await saveAll(next);
    set({ list: next });
    void deleteOne(id, set);
  },

  syncFromServer: async () => {
    if (!supabase) return;
    const user = useAuthStore.getState().user;
    if (!user) return;
    set({ syncing: true });
    try {
      // Push local rows first so they're not lost on first sign-in.
      const local = get().list;
      if (local.length > 0) {
        const rows = local.map((w) => toDbRow(w, user.id));
        const { error: pushErr } = await supabase
          .from('user_waypoints')
          .upsert(rows, { onConflict: 'owner_id,client_id' });
        if (pushErr) {
          set({ syncing: false, syncError: pushErr.message });
          return;
        }
      }
      const { data, error } = await supabase
        .from('user_waypoints')
        .select('*')
        .eq('owner_id', user.id);
      if (error) {
        set({ syncing: false, syncError: error.message });
        return;
      }
      const fromServer = (data as WaypointRowDb[]).map(fromDbRow);
      await saveAll(fromServer);
      set({ list: fromServer, syncing: false, syncError: null, lastSyncAt: Date.now() });
    } catch (e: unknown) {
      set({ syncing: false, syncError: e instanceof Error ? e.message : String(e) });
    }
  },

  syncToServer: async () => {
    if (!supabase) return;
    const user = useAuthStore.getState().user;
    if (!user) return;
    set({ syncing: true });
    try {
      const rows = get().list.map((w) => toDbRow(w, user.id));
      const { error } = await supabase
        .from('user_waypoints')
        .upsert(rows, { onConflict: 'owner_id,client_id' });
      if (error) set({ syncing: false, syncError: error.message });
      else set({ syncing: false, syncError: null, lastSyncAt: Date.now() });
    } catch (e: unknown) {
      set({ syncing: false, syncError: e instanceof Error ? e.message : String(e) });
    }
  },

  nearest: (lat, lon, thresholdNm = 0.1) => {
    const cosLat = Math.cos((lat * Math.PI) / 180);
    let best: UserWaypoint | null = null;
    let bestDist = Infinity;
    for (const w of get().list) {
      const dLat = w.lat - lat;
      const dLon = (w.lon - lon) * cosLat;
      const distNm = Math.sqrt(dLat * dLat + dLon * dLon) * 60;
      if (distNm < bestDist && distNm <= thresholdNm) {
        bestDist = distNm;
        best = w;
      }
    }
    return best;
  },

  within: (lat, lon, radiusNm) => {
    const cosLat = Math.cos((lat * Math.PI) / 180);
    return get()
      .list
      .map((w) => {
        const dLat = w.lat - lat;
        const dLon = (w.lon - lon) * cosLat;
        const distanceNm = Math.sqrt(dLat * dLat + dLon * dLon) * 60;
        return { ...w, distanceNm };
      })
      .filter((w) => w.distanceNm <= radiusNm)
      .sort((a, b) => a.distanceNm - b.distanceNm);
  },
}));

// Auto-sync on sign-in (same pattern as pilotStore).
useAuthStore.subscribe((state, prev) => {
  if (!prev.user && !!state.user) {
    void useWaypointStore.getState().syncFromServer();
  }
});
